// Real API Order Types
export interface BespokeDetails {
  garmentType: string
  fabricChoice: string
  sizeAndFitPreference: string
  color: string
  sellingPrice: number
  costPrice: number
  quantity: number
  estimatedDeliveryDate: string
  deliveryType: 'DELIVERY' | 'PICKUP'
  deliveryFee: number
  imageUrls?: string[]
}

export interface ApiOrderItem {
  id: string
  quantity: number
  price: number
  productVariant: {
    id: string
    productColour: {
      id: string
      quantity: number
      weight: number
      imageUrls: string[]
      colour: {
        id: string
        name: string
        hexValue: string
      }
      productMaterial: {
        id: string
        material: {
          id: string
          name: string
        }
        productSize: {
          id: string
          size: {
            id: string
            name: string
          }
          product: {
            id: string
            name: string
            basePrice: number
          }
        }
      }
    }
  }
}

export interface ApiOrder {
  id: string;
  customerId: string;
  adminId: string;
  merchantId: string;
  total: number;
  totalWeight: number;
  totalCostPrice: number;
  trackingId: string;
  friendlyOrderId?: string;
  paymentReference: string | null;
  orderType?: 'STANDARD' | 'BESPOKE';
  contactPhone?: string;
  shippingMethod?: string;
  shippingCost?: number;
  /** DHL-generated tracking number, set when shipment is booked via DHL */
  dhlTrackingNumber?: string;
  /** Carrier name for manual (non-DHL) shipments */
  carrier?: string;
  /** Tracking number for manual (non-DHL) shipments */
  manualTrackingNumber?: string;
  bespokeDetails?: BespokeDetails;
  status: // | "PAID"
  | "PENDING"
    | "ORDER_CONFIRMED"
    | "ORDER_RECEIVED"
    | "TRANSFER_FAILED"
    | "REFUNDED"
    | "PROCESSING"
    | "EN_ROUTE"
    | "DELIVERED"
    | "CANCELLED"
    | "RETURNED";
  additionalInfo: string;
  createdAt: string;
  updatedAt: string;
  deliveryDate: string | null;
  customer: {
    id: string;
    fullName: string;
    email: string;
  };
  items: ApiOrderItem[];
  address: {
    id: string;
    streetLines: string[];
    city: string;
    stateOrProvinceCode: string;
    postalCode: string;
    countryCode: string;
    placeId: string | null;
    formattedAddress: string;
    createdAt: string;
    updatedAt: string;
    phone?: string;
  };
}

export interface ApiOrdersResponse {
  data: ApiOrder[]
  meta: {
    hasNextPage: boolean
    hasPreviousPage: boolean
    startCursor: string
    endCursor: string
    totalCount: number
    lastCursor: string
  }
  links: {
    first: string
    last: string
  }
  execution_time_ms: number
}

// Frontend Order Types (for display compatibility)
export interface OrderItem {
  id: string
  name: string
  costPrice: number
  quantity: number
  unitPrice: number
  total: number
  size: string
  color: string
  material: string
}

export interface Order {
  id: string;
  merchantId: string;
  orderId: string;
  friendlyOrderId?: string;
  contactPhone?: string; // Optional field for backward compatibility
  address?: {
    phone?: string; // Optional field for backward compatibility
  }
  /** DHL-generated tracking number */
  dhlTrackingNumber?: string;
  /** Carrier name for manual (non-DHL) shipments */
  carrier?: string;
  /** Tracking number for manual (non-DHL) shipments */
  manualTrackingNumber?: string;
  /** Shipping method label (e.g. "delivery", "pickup") */
  shippingMethod?: string;
  date: string;
  orderType?: 'STANDARD' | 'BESPOKE';
  bespokeDetails?: BespokeDetails;
  customer: {
    name: string;
    email: string;
    phone: string;
  };

  items: {
    count: number;
    description: string;
    details: OrderItem[];
  };
  total: string;
  totalAmount: number;
  status: // | "PAID"
    | "PENDING"
    | "ORDER_CONFIRMED"
    | "ORDER_RECEIVED"
    | "TRANSFER_FAILED"
    | "REFUNDED"
    | "PROCESSING"
    | "EN_ROUTE"
    | "DELIVERED"
    | "CANCELLED"
    | "RETURNED";
  payment: "Pending" | "Failed" | "Refunded" | "Paid";
  category: "Agbada" | "Kaftan" | "Suits" | "Traditional" | "Accessories";
  location: string;
  shipping: {
    estimatedDelivery: string;
    address: {
      street: string;
      city: string;
      country: string;
      phone: string;
    };
  };
  paymentDetails: {
    method: string;
    totalAmount: number;
    processingFee: number;
    netAmount: number;
  };
  internalNotes: string;
}

// API Parameters
export interface OrdersParams {
  "page[size]"?: number;
  "page[after]"?: string;
  "page[before]"?: string;
  sort?: string[];
  "filter[status]"?:
    | "PAID"
    | "PENDING"
    | "ORDER_CONFIRMED"
    | "ORDER_RECEIVED"
    | "TRANSFER_FAILED"
    | "REFUNDED"
    | "PROCESSING"
    | "EN_ROUTE"
    | "DELIVERED"
    | "CANCELLED"
    | "RETURNED";
  "filter[user.email]"?: string;
  "filter[user.fullName]"?: string;
  "filter[items.itemName]"?: string;
  "filter[category]"?: string;
}

import { formatPrice } from '../../../shared/utils/priceUtils'

// Transform function to convert API order to frontend order
export function transformApiOrderToOrder(apiOrder: ApiOrder): Order {
  // Transform items
  const items: OrderItem[] = (apiOrder.items || []).map(item => ({
    id: item.id,
    name: item.productVariant.productColour.productMaterial.productSize.product.name,
    costPrice: item.productVariant.productColour.productMaterial.productSize.product.basePrice,
    quantity: item.quantity,
    unitPrice: item.price,
    total: item.price * item.quantity,
    size: item.productVariant.productColour.productMaterial.productSize.size.name,
    color: item.productVariant.productColour.colour.name,
    material: item.productVariant.productColour.productMaterial.material.name
  }))

  // Map API status to frontend status
  const statusMap: Record<ApiOrder["status"], Order["status"]> = {
    // 'PAID': 'PROCESSING',
    PENDING: "PROCESSING",
    ORDER_CONFIRMED: "ORDER_CONFIRMED",
    ORDER_RECEIVED: "ORDER_RECEIVED",
    PROCESSING: "PROCESSING",
    EN_ROUTE: "EN_ROUTE",
    DELIVERED: "DELIVERED",
    TRANSFER_FAILED: "TRANSFER_FAILED",
    REFUNDED: "REFUNDED",
    CANCELLED: "CANCELLED",
    RETURNED: "RETURNED",
  };

  let totalAmount: number;
  if (apiOrder.total == null) {
    console.warn(`Warning: apiOrder.total is ${apiOrder.total} for order id ${apiOrder.id}. Defaulting to 0. This may indicate a data quality issue.`);
    totalAmount = 0;
  } else {
    totalAmount = apiOrder.total;
  }

  const isBespoke = apiOrder.orderType === 'BESPOKE'

  return {
    id: apiOrder.id,
    merchantId: apiOrder.merchantId,

    orderId: apiOrder.trackingId,
    friendlyOrderId: apiOrder.friendlyOrderId,
    date: apiOrder.createdAt,
    orderType: apiOrder.orderType,
    bespokeDetails: apiOrder.bespokeDetails,
    dhlTrackingNumber: apiOrder.dhlTrackingNumber,
    carrier: apiOrder.carrier,
    manualTrackingNumber: apiOrder.manualTrackingNumber,
    shippingMethod: apiOrder.shippingMethod,
    customer: {
      name: apiOrder.customer?.fullName || "Unknown Customer",
      email: apiOrder.customer?.email || "No Email",
      phone: apiOrder.contactPhone || apiOrder.address?.phone || "N/A",
    },
    items: {
      count: isBespoke ? (apiOrder.bespokeDetails?.quantity ?? 0) : items.length,
      description: isBespoke ? (apiOrder.bespokeDetails?.garmentType ?? 'Bespoke Order') : items.map((item) => item.name).join(", "),
      details: items,
    },
    total: formatPrice(totalAmount),
    totalAmount: totalAmount,
    status: statusMap[apiOrder.status],
    payment: apiOrder.paymentReference ? "Paid" : "Pending", // Default - not provided by API
    category: "Traditional", // Default - would need to be determined from product data
    location:
      apiOrder.address?.city || apiOrder.address?.stateOrProvinceCode || "N/A",
    shipping: {
      estimatedDelivery: apiOrder.deliveryDate ?? "Not Specified", // Not provided by API
      address: {
        street: apiOrder.address?.streetLines?.[0] || "",
        city: apiOrder.address?.city || "",
        country: apiOrder.address?.countryCode || "",
        phone: apiOrder.address?.phone || "N/A",
      },
    },
    paymentDetails: {
      method: "N/A",
      totalAmount: totalAmount,
      processingFee: 0,
      netAmount: totalAmount,
    },
    internalNotes: apiOrder.additionalInfo,
  };
}